// The music pill (top right): LIVE badge, now-playing metadata, and the
// sound toggle. Hidden entirely while the booth is dark.

import type { ClubAudio } from "../audio/club-audio";
import type { MusicState } from "../core/world";

export class MusicPill {
  private root: HTMLDivElement;
  private track: HTMLSpanElement;
  private button: HTMLButtonElement;

  constructor(parent: HTMLElement, audio: ClubAudio) {
    this.root = document.createElement("div");
    this.root.className = "music-pill";
    this.root.hidden = true;
    this.root.innerHTML = `
      <span class="live-dot"></span><span class="live-label">LIVE</span>
      <span class="track"></span>
      <button class="sound-toggle" type="button"></button>
    `;
    parent.append(this.root);

    this.track = this.root.querySelector(".track") as HTMLSpanElement;
    this.button = this.root.querySelector(".sound-toggle") as HTMLButtonElement;

    // The window-level first-gesture unlock fires on this same click (its
    // pointerdown), so by click time the audio may already be playing.
    // Sample the state on pointerdown — before the global unlock runs — to
    // tell "this click unlocked the sound" apart from "this click mutes it".
    let audibleBeforeGesture = false;
    this.button.addEventListener("pointerdown", () => {
      audibleBeforeGesture = audio.audible;
    });
    this.button.addEventListener("click", () => {
      if (audibleBeforeGesture) {
        audio.toggle(); // was playing before this gesture → mute
      } else {
        if (!audio.isEnabled) audio.toggle(); // unmute if muted
        audio.unlock();
      }
      this.render(audio);
    });

    this.audio = audio;
  }

  private audio: ClubAudio;

  /** Called every frame with the current music state. */
  update(music: MusicState): void {
    this.root.hidden = !music.live;
    if (!music.live) return;
    const label = music.nowPlaying || "someone's at the booth";
    if (this.track.textContent !== label) this.track.textContent = label;
    this.render(this.audio);
  }

  private render(audio: ClubAudio): void {
    const needsGesture = audio.isLive && audio.isEnabled && !audio.audible;
    const next = needsGesture ? "🔊 tap for sound" : audio.audible ? "🔊" : "🔇";
    if (this.button.textContent !== next) this.button.textContent = next;
    this.button.classList.toggle("nudge", needsGesture);
  }
}
